import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useLazyQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button } from "@cofano/cck";
import { Box } from "@mui/material";

import { TableId, useFrontendTable } from "components/table";
import useLayout from "components/layout/useLayout";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import Helmet from "components/helmet/Helmet";

import { Language, DataFormat } from "imports/Utils";
import { FormattedDate } from "utils/DateUtils";

import AvailabilitySideviewer from "./components/sideviewer/AvailabilitySideviewer";
import AvailabilityCreateEditDialog from "./components/dialogs/AvailabilityCreateEditDialog";

import { LocationAvailabilityType } from "__generated__/graphql";
import { GetLocationDocument, SearchLocationsDocument } from "__generated__/view/locations/graphql/location.queries.generated";
import { GetLocationAvailabilitiesDocument, GetLocationAvailabilitiesQuery } from "__generated__/view/availability/graphql/quayAvailability.queries.generated";

type LocationAvailability = NonNullable<GetLocationAvailabilitiesQuery["locationAvailabilities"][number]>;

const QuayAvailabilities = () => {

	const navigate = useNavigate();
	const params = useParams();
	const layout = useLayout();

	const [ getLocation ] = useLazyQuery(GetLocationDocument);
	const [ getLocations ] = useLazyQuery(SearchLocationsDocument, {
		variables: {
			locationSearch: {},
			pagination: {
				page: 0,
				size: 1,
			},
		},
	});

	const [ location, setLocation ] = useState<LocationSelectOption | null>(null);
	const [ availabilites, setAvailabilities ] = useState<LocationAvailability[]>([]);
	const [ dialogOpen, openDialog ] = useState(false);

	const [ getAvailabilities, { refetch, loading } ] = useLazyQuery(GetLocationAvailabilitiesDocument, {
		onCompleted: (response) => {
			setAvailabilities(response?.locationAvailabilities ?? []);
		},
	});

	const { tableProps, refetch: tableRefetch, refetchAfterDelete } = useFrontendTable({
		id: TableId.QuayAvailabilities,
		content: availabilites,
	});

	useEffect(() => {
		if (location?.id) {
			tableRefetch(0);

			getAvailabilities({
				variables: {
					search: {
						ids: [ location.id ],
						timeWindow: {
							from: moment().add(-1, "month").unix() * 1000,
							to: moment().add(1, "month").unix() * 1000,
						},
					},
				},
			});
		}
	}, [ location?.id ]);

	useEffect(() => {
		if (params?.location) {
			getLocation({
				variables: {
					id: Number(params.location),
				},
			}).then(({ data }) => {
				setLocation(data?.location ?? null);
			});
		} else {
			getLocations().then(({ data }) => {
				setLocation(data?.searchLocations?.content?.[0] ?? null);
			});
		}
	}, []);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("QUAY_AVAILABILITY")}
					<Helmet title={intl.formatMessage({ id: "QUAY_AVAILABILITY" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Box sx={{ mr: 2, width: 300 }}>
						<LocationSelect
							value={location}
							onChange={(option) => setLocation(option)}
							clearable={false} />
					</Box>

					<Button onClick={() => navigate(`/view/locations/${location?.id}/details`)} disabled={!location} variant="text">
						{location?.description ?? intl.formatMessage({ id: "LOCATION" })}
					</Button>

					<Button onClick={() => openDialog(true)} disabled={!location}>
						{intl.formatMessage({ id: "ADD" })}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					onRowClick={(row) => {

						if (location && row.id) {
							layout.setDrawerSettings({
								open: true,
								title: intl.formatMessage({ id: "QUAY_AVAILABILITY" }),
								content: (
									<Box p={2}>
										<AvailabilitySideviewer
											location={location}
											availabilityId={row.id}
											onUpdate={() => refetch()}
											onDelete={() => {
												refetch();
												refetchAfterDelete();
												layout.closeDrawer();
											}} />
									</Box>
								),
								size: "normal",
								variant: "overlay",
							});
						}
					}}
					columns={[ {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						render: (row: LocationAvailability) => row.type === LocationAvailabilityType.CraneAvailability ? Language("MOORING_AVAILABILITIES_CRANE") : Language("MOORING"),
						width: 150,
					}, {
						id: "crane.name",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_CRANE" }),
						sortable: true,
						filterComponent: "TEXT",
						width: 150,
					}, {
						id: "amountOfCranes",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_CRANES_AMOUNT" }),
						sortable: true,
					}, {
						id: "craneSpeed",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_CRANE_SPEED" }),
						sortable: true,
						render: (row: LocationAvailability) => DataFormat.simpleUnit(row.craneSpeed ?? 0, DataFormat.UNITS.MOVH),
					}, {
						id: "fromDateTime",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_FROM" }),
						sortable: true,
						render: (row: LocationAvailability) => <FormattedDate date={row.fromDateTime} />,
						width: 150,
					}, {
						id: "toDateTime",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_TO" }),
						sortable: true,
						render: (row: LocationAvailability) => <FormattedDate date={row.toDateTime} />,
						width: 150,
					}, {
						id: "bolderFrom.description",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_BOLDER_FROM" }),
						sortable: true,
						render: (row: LocationAvailability) => row.bolderFrom ? `${row.bolderFrom.description} - ${row.bolderFrom.sequenceNumber}` : "",
						width: 150,
					}, {
						id: "bolderTo.description",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_BOLDER_TO" }),
						sortable: true,
						render: (row: LocationAvailability) => row.bolderTo ? `${row.bolderTo.description} - ${row.bolderTo.sequenceNumber}` : "",
						width: 150,
					}, {
						id: "maxWidthInMeters",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_MAX_WIDTH" }),
						sortable: true,
						render: (row: LocationAvailability) => DataFormat.simpleUnit(row.maxWidthInMeters ?? 0, DataFormat.UNITS.METER),
					}, {
						id: "canHandleOOG",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_CAN_HANDLE_OOG" }),
						render: (row: LocationAvailability) => DataFormat.boolean(row.canHandleOOG),
					}, {
						id: "canHandleBundles",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_CAN_HANDLE_BUNDLES" }),
						render: (row: LocationAvailability) => DataFormat.boolean(row.canHandleBundles),
					}, {
						id: "fullContainersAllowed",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_CAN_HANDLE_FULL_CONTAINERS" }),
						render: (row: LocationAvailability) => DataFormat.boolean(row.fullContainersAllowed),
					}, {
						id: "twinningAllowed",
						title: intl.formatMessage({ id: "MOORING_AVAILABILITIES_TWINNING_ALLOWED" }),
						render: (row: LocationAvailability) => DataFormat.boolean(row.twinningAllowed),
					}, {
						id: "overflowLocations",
						title: intl.formatMessage({ id: "LOCATION_AVAILABILITIES_OVERFLOW_LOCATIONS" }),
						render: (row: LocationAvailability) => DataFormat.boolean(row.overflowLocations && row.overflowLocations.length > 0),
					} ]} />
			</Card>

			{location && (
				<AvailabilityCreateEditDialog
					open={dialogOpen}
					onClose={() => openDialog(false)}
					defaultLocation={location}
					refetch={refetch} />
			)}
		</div>
	);
};

export default QuayAvailabilities;
